import "./MapTemplate.css";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";

import { MapStyleSwitcher } from "../../components/molecules/mapStyleSwitcher/MapStyleSwitcher";
import "../../components/molecules/mapStyleSwitcher/MapStyleSwitcher.css";

import FireRiskLegend from "../../components/molecules/fireRiskLegend/FireRiskLegend";
import ForecastTimeline from "../../components/molecules/forecastTimeline/ForecastTimeline";
import AlertCTA from "../../components/molecules/alertCTA/AlertCTA";

// ── Responsive zoom (KEEP)
const getResponsiveZoom = () => {
  const width = window.innerWidth;

  if (width < 768) return { initialZoom: 4.2, minZoom: 4.0 };
  if (width < 1024) return { initialZoom: 4.8, minZoom: 4.5 };
  return { initialZoom: 5.5, minZoom: 5.5 };
};

const MapTemplate = ({
  mapRef,
  onToggleRiskMap,
  riskMapVisible,
  onMapLoaded,
  forecastOffset,
  setForecastOffset,
  visibleLevels,
  onToggleLevel,
  layerOpacity,
  onOpacityChange,
  departements,
  onToggleDepartements,
}) => {
  const mapContainerRef = useRef(null);
  const navigate = useNavigate();

  const [legendVisible, setLegendVisible] = useState(true);
  const [isStyleLoaded, setIsStyleLoaded] = useState(false);

  const [zoomConfig] = useState(getResponsiveZoom());

  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  // ─────────────────────────────────────────────
  // 🔥 FIX 1: INIT MAP ONCE ONLY (LIKE MINI MAP)
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [2.2137, 46.2276],
      zoom: zoomConfig.initialZoom,
      minZoom: zoomConfig.minZoom,
      maxBounds: [
        [-12.0, 35.0],
        [18.0, 57.0],
      ],
      attributionControl: false,
    });

    mapRef.current = map;

    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: true }),
      "bottom-right"
    );

    map.addControl(new mapboxgl.FullscreenControl(), "bottom-right");

    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "bottom-right"
    );

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
      placeholder:
        "Rechercher des coordonnées, des régions ou des zones à risque...",
      marker: false,
      countries: "fr",
    });

    map.addControl(geocoder, "top-right");
    map.addControl(new MapStyleSwitcher(), "bottom-right");

    // legend toggle (KEEP YOUR CODE)
    class LegendToggleControl {
      constructor(onToggle) {
        this._onToggle = onToggle;
      }
      onAdd(map) {
        this._container = document.createElement("div");
        this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group";

        this._container.innerHTML = `
          <button type="button" aria-label="Légende">
            <span class="mapboxgl-ctrl-icon legend-toggle-icon"></span>
          </button>
        `;

        this._container.addEventListener("click", () => {
          this._onToggle();
        });

        return this._container;
      }
      onRemove() {
        this._container.remove();
      }
    }

    const legendToggle = new LegendToggleControl(() =>
      setLegendVisible((v) => !v)
    );

    map.addControl(legendToggle, "bottom-right");

    map.on("load", () => {
      setIsStyleLoaded(true);
      onMapLoaded?.();
    });

    map.on("style.load", () => {
      setIsStyleLoaded(true);
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []); // ✅ IMPORTANT FIX (NO zoom dependency)

  // ─────────────────────────────────────────────
  // FIX 2: APPLY ZOOM WITHOUT RECREATING MAP
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMinZoom(zoomConfig.minZoom);
  }, [zoomConfig]);

  return (
    <div className="map-container">
      <div ref={mapContainerRef} className="map-container">

      <div className="map-top-bar">
        <button className="map-back-btn" onClick={handleBack}>
          ← Retour
        </button>
      </div>

      <AlertCTA />

      {legendVisible && (
        <div className="legend-wrapper">
          <FireRiskLegend
            visibleLevels={visibleLevels}
            onToggleLevel={onToggleLevel}
            layerOpacity={layerOpacity}
            onOpacityChange={onOpacityChange}
            departementsVisible={departements.show}
            onToggleDepartements={onToggleDepartements}
            onClose={() => setLegendVisible(false)}
          />
        </div>
      )}

      <ForecastTimeline
        forecastOffset={forecastOffset}
        setForecastOffset={setForecastOffset}
      />
    </div>
    </div>
  );
};

export default MapTemplate;